import { useMemo } from "react";
import { ResponsiveTreeMap } from "@nivo/treemap";
import { BreadTransaction } from "breadcommon";
import { buildClasses } from "../utils/buildClasses";
import { RyeChartTooltipProvider } from "../rye/RyeChartTooltip";
import {
  BreakdownSelection,
  BreakdownTooltip,
  CHART_TOOLTIP_THEME,
  shadeFor,
  textColorFor,
  UnreviewedNote,
  useAvailableChartHeight,
} from "./breakdownChartParts";
import {
  BreakdownLevel,
  BreakdownSlice,
  BreakdownType,
  groupBreakdownSlices,
} from "./useCategoryBreakdownData";

const MIN_TREEMAP_HEIGHT_PX = 260;
// Below the treemap, for the unreviewed note.
const NOTE_HEIGHT_PX = 28;
const LABEL_FONT_SIZE_PX = 12;

interface TreemapDatum {
  id: string;
  label: string;
  color: string;
  textColor: string;
  transactions: BreadTransaction[];
  value?: number;
  children?: TreemapDatum[];
}

/**
 * Every slice as a box sized by its amount, so there's no need for "Other".
 * At the category level, categories sit inside a box for their group.
 *
 * Boxes are shaded by their group's rank, so a group's categories share its
 * color and the groups stay apart from each other.
 */
function buildTree(
  slices: BreakdownSlice[],
  type: BreakdownType,
  level: BreakdownLevel,
): TreemapDatum {
  const leaf = (slice: BreakdownSlice, rank: number): TreemapDatum => ({
    id: slice.id,
    label: slice.label,
    color: shadeFor(type, rank),
    textColor: textColorFor(rank),
    transactions: slice.transactions,
    value: slice.value,
  });

  const children =
    level === "groups"
      ? slices.map(leaf)
      : groupBreakdownSlices(slices).map((group, rank) => ({
          // Prefixed so a group can't share an id with one of its categories.
          id: `group:${group.id}`,
          label: group.label,
          color: shadeFor(type, rank),
          textColor: textColorFor(rank),
          transactions: group.slices.flatMap((slice) => slice.transactions),
          children: group.slices.map((slice) => leaf(slice, rank)),
        }));

  return {
    id: "root",
    label: "",
    color: "transparent",
    textColor: "transparent",
    transactions: [],
    children,
  };
}

export function BreakdownTreemap({
  slices,
  type,
  level,
  currency,
  unreviewedCount,
  onSelect,
}: {
  slices: BreakdownSlice[];
  type: BreakdownType;
  level: BreakdownLevel;
  currency: string;
  unreviewedCount: number;
  onSelect: (selection: BreakdownSelection) => void;
}): JSX.Element {
  const availableHeightPx = useAvailableChartHeight();
  const heightPx = Math.max(
    MIN_TREEMAP_HEIGHT_PX,
    availableHeightPx - (unreviewedCount > 0 ? NOTE_HEIGHT_PX : 0),
  );

  const tree = useMemo(
    () => buildTree(slices, type, level),
    [slices, type, level],
  );
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <div className={buildClasses("flex", "flex-col")}>
      <RyeChartTooltipProvider
        style={{ height: `${heightPx}px` }}
        className={buildClasses("[&_rect]:cursor-pointer")}
      >
        <ResponsiveTreeMap<TreemapDatum>
          data={tree}
          identity="id"
          value="value"
          // The root is only a container; its children are the top level.
          leavesOnly={false}
          tile="squarify"
          innerPadding={2}
          outerPadding={0}
          colors={(node) => node.data.color}
          nodeOpacity={1}
          borderWidth={1}
          borderColor="#ffffff"
          enableLabel={true}
          label={(node) => node.data.label}
          labelSkipSize={28}
          orientLabel={false}
          labelTextColor={(node) => node.data.textColor}
          enableParentLabel={level === "categories"}
          parentLabel={(node) => node.data.label}
          parentLabelSize={20}
          parentLabelPosition="top"
          parentLabelPadding={6}
          parentLabelTextColor={(node) => node.data.textColor}
          theme={{
            ...CHART_TOOLTIP_THEME,
            labels: {
              text: {
                fontFamily: "InterVariable, sans-serif",
                fontSize: LABEL_FONT_SIZE_PX,
              },
            },
          }}
          onClick={(node) => {
            if (node.id === "root") return;
            onSelect({
              id: node.data.id,
              label: node.data.label,
              transactions: node.data.transactions,
            });
          }}
          tooltip={({ node }) =>
            node.id === "root" ? null : (
              <BreakdownTooltip
                color={node.data.color}
                label={node.data.label}
                value={node.value}
                total={total}
                currency={currency}
              />
            )
          }
          role="img"
        />
      </RyeChartTooltipProvider>
      <UnreviewedNote
        count={unreviewedCount}
        className={buildClasses("pt-3")}
      />
    </div>
  );
}
